import { Alert } from "@mui/material";
import type {
  UseInfiniteQueryResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { FormattedMessage } from "react-intl";

import { Spinner } from "@citrine/atoms/loading";
import { getMergedStatus } from "@citrine/client";

type DataArray = unknown[];
type Queries<Data extends DataArray> = {
  [K in keyof Data]:
    | UseQueryResult<Data[K]>
    | UseInfiniteQueryResult<Data[K]>
    | null
    | undefined;
};

interface QuerySuspenseProps<Data extends DataArray> {
  queries: Queries<Data>;
  error?: React.ReactNode;
  loading?: React.ReactNode;
  render: (data: Data) => React.ReactNode;
}

const ERROR = (
  <Alert severity="error">
    <FormattedMessage id="query.error" />
  </Alert>
);
const LOADING = <Spinner />;

export function QuerySuspense<Data extends DataArray>({
  queries,
  error = ERROR,
  loading = LOADING,
  render,
}: QuerySuspenseProps<Data>) {
  const status = getMergedStatus(queries.filter(Boolean));

  return status === "error"
    ? error
    : status === "success"
      ? render(queries.map((q) => q?.data) as Data)
      : loading;
}
